import React, { useMemo, useState } from 'react';
import { Network as NetworkIcon, GitBranch } from 'lucide-react';

const DependencyNetworkGraph = ({ data }) => {
  const [selectedNode, setSelectedNode] = useState(null);

  const getHealthColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 60) return '#eab308';
    if (score >= 30) return '#f97316';
    return '#ef4444';
  };

  // Process data to create network structure
  const networkData = useMemo(() => {
    const nodes = [];
    let totalUpstream = 0;
    let totalDownstream = 0;

    data.slice(0, 15).forEach((item, idx) => {
      const rawData = item.raw_data || {};
      const relationships = rawData.relationships || {};
      const upstream = relationships.upstream || [];
      const downstream = relationships.downstream || [];

      totalUpstream += upstream.length;
      totalDownstream += downstream.length;

      nodes.push({
        id: item.changeNumber,
        changeNumber: item.changeNumber,
        description: item.description,
        health: item.health,
        healthScore: item.healthScore,
        color: getHealthColor(item.healthScore),
        upstreamCount: upstream.length,
        downstreamCount: downstream.length,
        totalDeps: upstream.length + downstream.length,
        upstream: upstream.slice(0, 3), // Limit for display
        downstream: downstream.slice(0, 3)
      });
    });

    return { nodes, totalUpstream, totalDownstream };
  }, [data]);

  return (
    <div className="bg-dark-card rounded-lg border border-dark-border p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <NetworkIcon size={24} className="text-cyan-accent" />
          <h3 className="text-xl font-bold text-cyan-accent">Dependency Network View</h3>
          <span className="text-sm text-gray-500">(Top 15 changes)</span>
        </div>
      </div>

      {/* Network Grid View */}
      <div className="space-y-3 max-h-[600px] overflow-y-auto">
        {networkData.nodes.map((node, idx) => (
          <div
            key={node.id}
            className={`bg-dark-border/20 rounded-lg p-4 border transition-all cursor-pointer ${
              selectedNode === node.id
                ? 'border-cyan-accent/60 bg-cyan-accent/5'
                : 'border-dark-border/60 hover:border-cyan-accent/30'
            }`}
            onClick={() => setSelectedNode(selectedNode === node.id ? null : node.id)}
          >
            {/* Node Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {/* Health Indicator */}
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: `${node.color}20`,
                    border: `2px solid ${node.color}`,
                    boxShadow: `0 0 15px ${node.color}40`
                  }}
                >
                  <span className="text-sm font-bold" style={{ color: node.color }}>
                    {node.healthScore}
                  </span>
                </div>

                {/* Change Info */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-white">{node.changeNumber}</div>
                  <div className="text-xs text-gray-500 truncate">{node.description}</div>
                  <div className="text-xs mt-1">
                    <span className={`px-2 py-0.5 rounded ${
                      node.health === 'HEALTHY' ? 'bg-lime-accent/20 text-lime-accent' :
                      node.health === 'DEGRADED' ? 'bg-yellow-accent/20 text-yellow-accent' :
                      node.health === 'UNHEALTHY' ? 'bg-red-500/20 text-red-500' :
                      'bg-gray-500/20 text-gray-500'
                    }`}>
                      {node.health}
                    </span>
                  </div>
                </div>

                {/* Dependency Counts */}
                <div className="flex gap-4 flex-shrink-0">
                  <div className="text-center">
                    <div className="text-xl font-bold text-purple-accent">{node.upstreamCount}</div>
                    <div className="text-xs text-gray-500">Upstream</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-orange-accent">{node.downstreamCount}</div>
                    <div className="text-xs text-gray-500">Downstream</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Expanded Dependencies */}
            {selectedNode === node.id && (node.upstream.length > 0 || node.downstream.length > 0) && (
              <div className="mt-3 pt-3 border-t border-dark-border/50 space-y-3">
                {/* Upstream Dependencies */}
                {node.upstream.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <GitBranch size={14} className="text-purple-accent" />
                      <span className="text-xs font-semibold text-purple-accent">
                        Upstream Dependencies ({node.upstreamCount})
                      </span>
                    </div>
                    <div className="space-y-1 ml-5">
                      {node.upstream.map((dep, i) => (
                        <div key={i} className="text-xs text-gray-400 flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-purple-accent"></div>
                          <span>{dep.displayName || dep.entityId || 'Unknown'}</span>
                        </div>
                      ))}
                      {node.upstreamCount > 3 && (
                        <div className="text-xs text-gray-600 ml-4">
                          +{node.upstreamCount - 3} more...
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Downstream Dependencies */}
                {node.downstream.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <GitBranch size={14} className="text-orange-accent" />
                      <span className="text-xs font-semibold text-orange-accent">
                        Downstream Dependencies ({node.downstreamCount})
                      </span>
                    </div>
                    <div className="space-y-1 ml-5">
                      {node.downstream.map((dep, i) => (
                        <div key={i} className="text-xs text-gray-400 flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-orange-accent"></div>
                          <span>{dep.displayName || dep.entityId || 'Unknown'}</span>
                        </div>
                      ))}
                      {node.downstreamCount > 3 && (
                        <div className="text-xs text-gray-600 ml-4">
                          +{node.downstreamCount - 3} more...
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center justify-center gap-6 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-lime-accent"></div>
          <span className="text-gray-400">Healthy (80-100)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-yellow-accent"></div>
          <span className="text-gray-400">Degraded (60-79)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-orange-accent"></div>
          <span className="text-gray-400">Warning (30-59)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <span className="text-gray-400">Critical (0-29)</span>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-4 pt-4 border-t border-dark-border/50 grid grid-cols-3 gap-4 text-center">
        <div>
          <div className="text-2xl font-bold text-cyan-accent">{networkData.nodes.length}</div>
          <div className="text-xs text-gray-400">Changes Shown</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-purple-accent">{networkData.totalUpstream}</div>
          <div className="text-xs text-gray-400">Upstream Links</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-orange-accent">{networkData.totalDownstream}</div>
          <div className="text-xs text-gray-400">Downstream Links</div>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-4 text-center text-xs text-gray-500">
        💡 Click on any change to expand and view its dependencies
      </div>
    </div>
  );
};

export default DependencyNetworkGraph;
